import {
  PortableText,
  type PortableTextComponents,
  type PortableTextBlock,
} from 'next-sanity';
import Image from 'next/image';
import Button from '../button/button';
import Link from 'next/link';
import { schemaTypeAndPageMapping } from '@/util';
import { imageBuilder, urlForImage } from '@/sanity/lib/utils';
import { Col, Row } from 'react-bootstrap';
import {
  SanityImageCrop,
  SanityImageHotspot,
  internalGroqTypeReferenceTo,
} from '@/sanity.types';
import VideoPlayer from '../videoPlayer/videoPlayer';
import { imageData } from '@/config/defaultBlur';

export function LandingPortableText({
  className,
  value,
  type,
}: {
  className?: string;
  value: PortableTextBlock[];
  type?: string;
}) {
  const components: PortableTextComponents = {
    marks: {
      link: ({ children, value }) => {
        return value?.href ? (
          <Link
            href={value.href}
            rel="noreferrer noopener"
            target="_blank"
            aria-label={`Go to ${value.href}`}
          >
            {children}
          </Link>
        ) : (
          <>{children}</>
        );
      },
      internalLink: ({ children, value }) => {
        const pageName = schemaTypeAndPageMapping(value?.type);
        const internalUrl = `/${pageName?.page}/${value?.slug}`;
        return internalUrl ? (
          <Link href={internalUrl} aria-label={`Go to ${internalUrl}`}>
            {children}
          </Link>
        ) : (
          <>{children}</>
        );
      },
      textColor: ({ children, value }) => {
        return <span style={{ color: value?.value }}>{children}</span>;
      },
      highlightColor: ({ children, value }) => {
        return (
          <span style={{ backgroundColor: value?.value }}>{children}</span>
        );
      },
    },
    types: {
      image: ({ value }) => {
        const { asset } = value;

        const imageUrl = imageBuilder
          .image(value)
          .auto('format')
          .width(800)
          .url();
        const { width, height } = asset?.metadata?.dimensions || {
          width: 500,
          height: 350,
        };
        const blurDataURL = asset?.metadata?.lqip || imageData.blurImage;
        return (
          <div className="text-center">
            <Image
              src={(imageUrl as string) || ''}
              alt={value?.alt}
              width={width}
              height={height}
              quality={100}
              sizes="100vw"
              placeholder="blur"
              blurDataURL={blurDataURL}
            />
            <p>{value?.caption}</p>
          </div>
        );
      },
      ctaButton: ({ value }) => {
        return type?.includes('hero') ? (
          <div className="hero-btn">
            <Button
              target="_blank"
              href={value?.link || ''}
              variant="secondary"
            >
              {value?.buttonText}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <Button
              target="_blank"
              href={value?.link || ''}
              {...(value?.buttonColor
                ? {
                    style: {
                      backgroundColor: value?.buttonColor?.value,
                      borderColor: value?.buttonColor?.value,
                    },
                  }
                : { variant: 'secondary' })}
            >
              {value?.buttonText}
            </Button>
          </div>
        );
      },
      code: ({ value }) => {
        if (!value?.code) return null;
        if (value?.language === 'html') {
          return (
            <div
              className="custom code"
              dangerouslySetInnerHTML={{
                __html: value?.code,
              }}
            />
          );
        }
        if (value?.language === 'javascript') {
          return <script>{value?.code}</script>;
        }

        if (value?.language === 'css') {
          return <style>{value?.code}</style>;
        }
        return (
          <div
            className="customCode"
            dangerouslySetInnerHTML={{ __html: value?.code }}
          />
        );
      },
      ctaGray: ({ value }) => {
        return (
          <div className="text-center">
            <div
              className={`csm-context ${value?.isGray ? 'csm-link-holder' : 'csm-no-bg'}`}
            >
              <span>{value?.text}</span>
              <Link
                href={value?.link || ''}
                target="_blank"
                aria-label={`Go to ${value?.link}`}
              >
                {value?.linkText}
              </Link>
            </div>
          </div>
        );
      },
      sampleSignUps: ({ value }) => {
        return (
          <div className="signupsWrap">
            <h3>{value?.title || 'Sample Sign Ups'}</h3>
            <Row className="mb-4" style={{ justifyContent: 'center' }}>
              {value?.thumbnails?.map(
                (
                  thumbnail: {
                    thumbnailLink?: string;
                    thumbnailImage: {
                      asset?: {
                        _ref: string;
                        _type: 'reference';
                        _weak?: boolean;
                        [internalGroqTypeReferenceTo]?: 'sanity.imageAsset';
                      };
                      hotspot?: SanityImageHotspot;
                      crop?: SanityImageCrop;
                      alt: string;
                      _type: 'image';
                    };
                    alt: string;
                  },
                  index: number
                ) => (
                  <Col xs={12} md={4} key={index}>
                    {thumbnail?.thumbnailLink ? (
                      <Link
                        href={thumbnail?.thumbnailLink || ''}
                        target="_blank"
                        aria-label={`Go to ${thumbnail?.thumbnailLink}`}
                      >
                        <Image
                          src={
                            thumbnail?.thumbnailImage
                              ? (urlForImage(
                                  thumbnail.thumbnailImage
                                )?.url() as string)
                              : ''
                          }
                          alt={thumbnail?.alt || 'signupsWrap CTA images'}
                          width={300}
                          height={250}
                          quality={100}
                          sizes="(max-width: 767px) 100vw, 50vw"
                          placeholder="blur"
                          blurDataURL={imageData.blurImage}
                        />
                      </Link>
                    ) : (
                      <Image
                        src={
                          thumbnail?.thumbnailImage
                            ? (urlForImage(
                                thumbnail.thumbnailImage
                              )?.url() as string)
                            : ''
                        }
                        alt={thumbnail?.alt || 'signupsWrap CTA images'}
                        width={300}
                        height={250}
                        quality={100}
                        sizes="(max-width: 767px) 100vw, 50vw"
                        placeholder="blur"
                        blurDataURL={imageData.blurImage}
                      />
                    )}
                  </Col>
                )
              )}
            </Row>
            {value?.includeSUPButton ? (
              <Button
                variant="secondary"
                size="lg"
                target="_blank"
                href="/index.cfm?go=w.Welcome"
              >
                Create Sign Up
              </Button>
            ) : (
              <></>
            )}
          </div>
        );
      },
      contentVideo: ({ value }) => {
        return (
          <div className="video-container">
            <VideoPlayer url={value?.url} width="100%" height="auto" />
          </div>
        );
      },
    },
  };

  return (
    <div className={['prose', className].filter(Boolean).join(' ')}>
      <PortableText components={components} value={value} />
    </div>
  );
}
